import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import {
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  tap,
} from 'rxjs';
import { InvitationToken } from '../../../../app/models/users/invitationToken.model';
import { AuthStateService } from '../auth-state/auth-state.service';
import { Router } from '@angular/router';

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  platform: number;
  password: string;
  token: string;
  idClient: string;
  lang: string;
  countryCode: string;
  phoneNumber: string;
  towFaMethod: string;
}

export interface LoginResponse {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  idClient: string;
  platform: number;
  lang: string;
  isGuest: boolean;
  verified2Fa: boolean;
  towFaMethod: string;
  phone: string;
  isAuthenticated: boolean;
}

export interface ValidateAuthResponse {
  verified2Fa: boolean;
  platform: number;
  authorities: string[];
}

export interface Verify2FaRequest {
  verificationCode: string;
}

@Injectable({
  providedIn: 'root',
})
export class RestUserService {
  private permissions$: Observable<string[]> | null = null;
  private auth$?: Observable<ValidateAuthResponse>;

  private baseUrl = `${environment.apiUrl}/internal/users`;
  private AuthBaseUrl = `${environment.apiUrl}/auth/internal`;
  private partner_v1_Url = `${environment.apiUrl}/api/partner/v1`;

  private router = inject(Router);

  constructor(
    private http: HttpClient,
    private authState: AuthStateService,
  ) {}

  login(
    email: string,
    password: string,
    rememberMe: boolean,
  ): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.AuthBaseUrl}/login`, {
      email,
      password,
      rememberMe,
    });
  }

  register(request: RegisterRequest): Observable<any> {
    return this.http.post(`${this.AuthBaseUrl}/register`, request);
  }

  logout(): Observable<any> {
    return this.http.post(`${this.AuthBaseUrl}/logout`, {});
  }

  logoutAndRedirect() {
    this.logout()
      .pipe(
        catchError((err) => {
          console.error('Logout failed', err);
          return of(null);
        }),
      )
      .subscribe(() => {
        this.clearSession();
        this.router.navigate(['/login']);
      });
  }

  send2FaCode(): Observable<boolean> {
    return this.http.post<boolean>(`${this.AuthBaseUrl}/2fa/send`, {});
  }

  verify2Fa(request: Verify2FaRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${this.AuthBaseUrl}/2fa/verify`,
      request,
    );
  }

  getInvitationToken(token: string): Observable<InvitationToken> {
    return this.http.get<InvitationToken>(
      `${this.AuthBaseUrl}/invitation-token`,
      {
        params: { token: token },
      },
    );
  }

  getUserAuthoritites(userId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.partner_v1_Url}/user-authorities`, {
      params: { user_id: userId },
    });
  }

  getCurrentUserAuthoritites(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/user-authorities`);
  }

  getCurrentUserRoles(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/user-roles`);
  }

  getPermissions(): Observable<string[]> {
    if (!this.permissions$) {
      this.permissions$ = forkJoin({
        authorities: this.http.get<any[]>(`${this.baseUrl}/user-authorities`),
        roles: this.http.get<string[]>(`${this.baseUrl}/user-roles`),
      }).pipe(
        map(({ authorities, roles }) => [
          ...authorities.map((a: any) => a.name ?? a),
          ...roles.map((a: any) => a.name ?? a),
        ]),
        tap((permissions) => {
          this.authState.setPermissions(permissions);
        }),
        shareReplay(1),
      );
    }
    return this.permissions$;
  }

  validateAuth(): Observable<ValidateAuthResponse> {
    if (!this.auth$) {
      this.auth$ = this.http
        .get<ValidateAuthResponse>(`${this.AuthBaseUrl}/validate-auth`)
        .pipe(
          tap((auth) => {
            this.authState.setAuth({
              verified2Fa: auth.verified2Fa,
              platform: auth.platform,
              authorities:[]
            });

            if (auth.authorities) {
              this.authState.setPermissions(auth.authorities);
            }
          }),
          shareReplay(1),
        );
    }
    return this.auth$;
  }

  getCsrf(): Observable<any> {
    return this.http.get(`${this.baseUrl}/csrf`);
  }

  clearCache() {
    const keysToRemove = ['selectedMenuId', 'token', 'isAuthenticated', 'lang'];
    keysToRemove.forEach((key) => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
      }
    });
    this.permissions$ = null;
    this.auth$ = undefined;
    this.authState.reset();
  }

  clearSession() {
    this.clearCache();
    this.authState.reset();
    localStorage.removeItem('userInfo');
    localStorage.removeItem('appLang');
  }
}
